<div class="w-full max-w-full p-4 lg:p-0">
  
  <!-- Header z licznikiem -->
  <div class="mb-6">
    <div class="flex items-center gap-3">
      <h2 class="text-2xl font-bold">Użytkownicy</h2>
      <span class="px-3 py-1 text-sm font-medium rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200">
        {{pagination.count}}
      </span>
    </div>
  </div>

  <!-- Search Bar Card -->
  <div class="bg-card rounded-2xl shadow-lg p-6 mb-6">
    <div class="flex items-center gap-4">
      <mat-form-field appearance="fill" class="flex-1">
        <mat-label>Szukaj użytkownika</mat-label>
        <input 
          [formControl]="searchInputControl" 
          type="text" 
          matInput 
          placeholder="Imię, nazwisko lub email">
        <mat-icon matPrefix [svgIcon]="'tabler_outline:search'"></mat-icon>
        <button 
          *ngIf="searchInputControl.value" 
          matSuffix 
          mat-icon-button 
          (click)="searchInputControl.setValue('')"
          matTooltip="Wyczyść wyszukiwanie">
          <mat-icon [svgIcon]="'tabler_outline:x'"></mat-icon>
        </button>
      </mat-form-field>
    </div>
  </div>

  <!-- Table Card -->
  <div class="bg-card rounded-2xl shadow-lg overflow-hidden">
    
    <!-- Loading overlay -->
    <div *ngIf="isLoading" class="flex items-center justify-center p-12">
      <mat-spinner diameter="50"></mat-spinner>
    </div>

    <!-- Empty state -->
    <div *ngIf="!isLoading && users.length === 0" class="p-12 text-center">
      <mat-icon class="text-6xl text-gray-400 mb-4" [svgIcon]="'tabler_outline:users'"></mat-icon>
      <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
        Brak użytkowników
      </h3>
      <p class="text-gray-500 dark:text-gray-400">
        Nie znaleziono użytkowników spełniających kryteria wyszukiwania
      </p>
    </div>

    <!-- Table -->
    <div *ngIf="!isLoading && users.length > 0" class="overflow-x-auto">
      <table mat-table [dataSource]="users" class="w-full">

        <!-- Numer -->
        <ng-container matColumnDef="number">
          <th mat-header-cell *matHeaderCellDef class="font-semibold w-20">#</th>
          <td mat-cell *matCellDef="let user; let i = index">
            <span class="text-gray-500 dark:text-gray-400 font-mono text-sm">
              {{ getUserNumber(i) }}
            </span>
          </td>
        </ng-container>

        <!-- Imię i nazwisko -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef class="font-semibold">Imię i nazwisko</th>
          <td mat-cell *matCellDef="let user">
            <div class="flex items-center gap-3">
              <!-- Avatar: logo firmy lub inicjały -->
              <div class="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200 font-semibold">
                <!-- Logo firmy -->
                <img 
                  *ngIf="user.company?.logoUrl" 
                  [src]="user.company.logoUrl" 
                  [alt]="user.company?.name || 'Logo'"
                  class="w-full h-full object-cover"
                  (error)="user.company.logoUrl = null">
                <!-- Inicjały -->
                <span *ngIf="!user.company?.logoUrl">
                  {{ (user.firstName?.charAt(0) || '') + (user.lastName?.charAt(0) || '') }}
                </span>
              </div>
              <span class="font-medium">
                {{ user.firstName }} {{ user.lastName }}
              </span>
            </div>
          </td>
        </ng-container>

        <!-- Email -->
        <ng-container matColumnDef="email">
          <th mat-header-cell *matHeaderCellDef class="font-semibold">Adres e-mail</th>
          <td mat-cell *matCellDef="let user">
            <span class="text-gray-600 dark:text-gray-300">{{ user.emailAddress }}</span>
          </td>
        </ng-container>

        <!-- Subscription -->
        <ng-container matColumnDef="subscription">
          <th mat-header-cell *matHeaderCellDef class="font-semibold">Subskrypcja</th>
          <td mat-cell *matCellDef="let user">
            <span 
              class="px-2 py-1 rounded-full text-xs font-semibold cursor-help"
              [ngClass]="getSubscriptionTypeColor(user.subscription?.stage)"
              [matTooltip]="getSubscriptionTooltip(user)"
              matTooltipClass="whitespace-pre-line">
              {{ getSubscriptionTypeLabel(user.subscription?.stage) }}
              <span 
                *ngIf="user.subscription?.validUntil && !isSubscriptionExpired(user.subscription.validUntil)"
                class="ml-1 opacity-70">
                ({{ getSubscriptionDaysRemaining(user.subscription.validUntil) }}d)
              </span>
              <span 
                *ngIf="user.subscription?.validUntil && isSubscriptionExpired(user.subscription.validUntil)"
                class="ml-1 text-red-500">
                ⚠
              </span>
            </span>
          </td>
        </ng-container>

        <!-- Actions -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="font-semibold text-right w-16"></th>
          <td mat-cell *matCellDef="let user" class="text-right" (click)="$event.stopPropagation()">
            <button 
              mat-icon-button
              color="primary"
              [routerLink]="user._id"
              matTooltip="Zobacz szczegóły użytkownika"
              class="!text-primary-600 dark:!text-primary-400">
              <mat-icon [svgIcon]="'tabler_outline:chevron-right'"></mat-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr 
          mat-row 
          *matRowDef="let row; columns: displayedColumns;" 
          [routerLink]="row._id"
          class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"></tr>
      </table>
    </div>

    <!-- Pagination -->
    <div *ngIf="!isLoading && users.length > 0" class="p-4 border-t dark:border-gray-700">
      <pagination 
        class="flex items-center justify-center" 
        [currentPage]="pagination.currentPage" 
        [totalPages]="pagination.pages" 
        (onPageChange)="onPageChange($event)">
      </pagination>
    </div>
  </div>

</div>